import { LOGIN_TYPE } from 'consts';
import { useGetAuthLoginMethods } from 'features/auth/api';
import { useEffect, useState } from 'react';
import type { LoginMethodType } from 'types';
import { ChooseLoginMethod } from './ChooseLoginMethod';
import { useSuccessLogin } from './hooks';
import { LoginOTP } from './LoginOTP';
import { LoginPassword } from './LoginPassword';
import { LoginSSO } from './LoginSSO';

type Props = {
  loginMethod: LoginMethodType | undefined;
  loginMethods: {
    [key in keyof typeof LOGIN_TYPE]: boolean;
  };
  backButtonAction: (() => void) | undefined;
  switchLoginMethod: () => void;
  extractAndSetLoginMethods: (l?: LoginMethodType[]) => void;
  identifier: string | undefined;
  isOnDocumentInvitationRoute: boolean;
  isOnBoardInvitationRoute: boolean;
  isOnNotificationRoute: boolean;
  isOnHintRoute: boolean;
};

export const Login = ({
  loginMethod,
  loginMethods,
  backButtonAction,
  switchLoginMethod,
  extractAndSetLoginMethods,
  identifier,
  isOnDocumentInvitationRoute,
  isOnBoardInvitationRoute,
  isOnNotificationRoute,
  isOnHintRoute,
}: Props) => {
  const [identifierValue, setIdentifierValue] = useState(identifier || '');

  const hasLoginMethodEmail = Boolean(loginMethods?.email_code);
  const hasLoginMethodSms = Boolean(loginMethods?.sms_code);
  const hasLoginMethodPassword = Boolean(loginMethods?.password);

  const onSuccess = useSuccessLogin();

  const { isLoading, data, error, mutate } = useGetAuthLoginMethods();

  const handleMutate = ({ identifier }: { identifier: string }) => {
    const isPhone = !identifier.includes('@');
    mutate(isPhone ? { phone: identifier } : { email: identifier });
  };

  useEffect(() => {
    if (identifier) {
      setIdentifierValue(identifier);
    }
  }, [identifier]);

  useEffect(() => {
    if (data && !isOnDocumentInvitationRoute) {
      extractAndSetLoginMethods(data.loginMethods);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (loginMethod) {
    switch (loginMethod) {
      case LOGIN_TYPE.password:
        return (
          <LoginPassword
            identifier={identifierValue}
            setIdentifier={setIdentifierValue}
            hasLoginMethodEmail={hasLoginMethodEmail}
            hasLoginMethodSms={hasLoginMethodSms}
            switchLoginMethod={switchLoginMethod}
            onSuccess={onSuccess}
            backButtonAction={backButtonAction}
          />
        );
      case LOGIN_TYPE.azure:
      case LOGIN_TYPE.google:
      case LOGIN_TYPE.okta:
        return (
          <LoginSSO
            email={identifierValue}
            provider={loginMethod as 'google' | 'azure' | 'okta'}
          />
        );
      case LOGIN_TYPE.email_code:
        return (
          <LoginOTP
            identifier={identifierValue}
            hasLoginMethodPassword={hasLoginMethodPassword}
            switchLoginMethod={switchLoginMethod}
            onSuccess={onSuccess}
            backButtonAction={backButtonAction}
            isOnDocumentInvitationRoute={isOnDocumentInvitationRoute}
          />
        );
      case LOGIN_TYPE.sms_code:
        return (
          <LoginOTP
            identifier={identifierValue}
            hasLoginMethodPassword={hasLoginMethodPassword}
            switchLoginMethod={switchLoginMethod}
            onSuccess={onSuccess}
            backButtonAction={backButtonAction}
            isOnDocumentInvitationRoute={isOnDocumentInvitationRoute}
          />
        );
      default:
        return (
          <LoginPassword
            identifier={identifierValue}
            setIdentifier={setIdentifierValue}
            hasLoginMethodEmail={hasLoginMethodEmail}
            hasLoginMethodSms={hasLoginMethodSms}
            switchLoginMethod={switchLoginMethod}
            onSuccess={onSuccess}
            backButtonAction={backButtonAction}
          />
        );
    }
  }

  /* prevents UI from blinking */
  if (
    isOnDocumentInvitationRoute ||
    isOnBoardInvitationRoute ||
    isOnNotificationRoute ||
    isOnHintRoute
  ) {
    return null;
  }

  return (
    <ChooseLoginMethod
      identifier={identifierValue}
      isLoading={isLoading}
      error={error}
      setIdentifier={setIdentifierValue}
      mutate={handleMutate}
    />
  );
};
